import React, { useContext, useEffect, useRef, useState } from "react"
import Logo from "../../images/gadait-icon.inline.svg"
import { Locale } from "../../context"
import { openSpotlight, prefetchSpotlight } from "../search/spotlightBus"

// Sous-navigation locale sticky façon pages produit Apple : burger + monogramme
// à gauche (accès menu site / accueil), nom de la page, chapitres au centre
// (section active surlignée), mini-CTA bleu.
// Mode « takeover » : tant que la barre n'est pas épinglée, elle se cale sous la
// barre native du site ; dès qu'elle s'épingle, la barre native coulisse vers le
// haut (classe html.gd-cn-takeover, écoutée par Header) et la ChapterNav devient
// l'unique barre, à top:0 — comportement des pages produit Apple.
const STYLE = `
  .cn-bar { position:sticky; z-index:40; background:rgba(255,255,255,0.86); backdrop-filter:saturate(180%) blur(20px); -webkit-backdrop-filter:saturate(180%) blur(20px); border-bottom:1px solid rgba(0,0,0,0.08); transition:top 300ms cubic-bezier(0.22,1,0.36,1); }
  @media (prefers-reduced-motion: reduce) { .cn-bar { transition:none; } }
  .cn-inner { max-width:1140px; margin:0 auto; padding:0 24px; display:flex; align-items:center; gap:20px; height:52px; }
  .cn-site { display:flex; align-items:center; gap:2px; flex-shrink:0; margin-left:-8px; }
  .cn-burger { display:inline-flex; align-items:center; justify-content:center; width:36px; height:36px; background:none; border:none; cursor:pointer; color:#1d1d1f; border-radius:999px; transition:background 200ms; }
  .cn-burger:hover { background:rgba(0,0,0,0.05); }
  .cn-burger span { position:relative; display:inline-flex; width:21px; height:12px; }
  .cn-burger span::before, .cn-burger span::after { content:""; position:absolute; left:0; width:21px; height:1.5px; background:currentColor; }
  .cn-burger span::before { top:2px; } .cn-burger span::after { bottom:2px; }
  .cn-home { display:inline-flex; align-items:center; justify-content:center; width:32px; height:36px; border-radius:999px; transition:background 200ms; }
  .cn-home:hover { background:rgba(0,0,0,0.05); }
  .cn-title { font-size:15px; font-weight:600; letter-spacing:-0.02em; color:#1d1d1f; white-space:nowrap; flex-shrink:0; }
  .cn-links { position:relative; display:flex; align-items:center; gap:2px; overflow-x:auto; scrollbar-width:none; -ms-overflow-style:none; flex:1; }
  .cn-links::-webkit-scrollbar { display:none; }
  .cn-link { font-size:13px; color:#6e6e73; text-decoration:none; white-space:nowrap; padding:6px 12px; border-radius:999px; transition:color 200ms, background 200ms; cursor:pointer; background:none; border:none; font-family:inherit; }
  .cn-link:hover { color:#1d1d1f; }
  .cn-link.active { color:#1d1d1f; background:rgba(0,0,0,0.05); font-weight:600; }
  .cn-search { display:inline-flex; align-items:center; justify-content:center; width:36px; height:36px; flex-shrink:0; background:none; border:none; cursor:pointer; color:#1d1d1f; border-radius:999px; transition:background 200ms; }
  .cn-search:hover { background:rgba(0,0,0,0.05); }
  .cn-cta { flex-shrink:0; font-size:13px; font-weight:500; color:#fff; background:#0071e3; border:none; border-radius:999px; padding:7px 16px; cursor:pointer; font-family:inherit; transition:background 200ms, transform 200ms; white-space:nowrap; }
  .cn-cta:hover { background:#0077ed; transform:translateY(-1px); }
  @media (max-width:640px){ .cn-title { display:none; } .cn-cta { display:none; } }
  /* Mobile : cibles tactiles ≥ 40 px sans changer le dessin (la pastille
     reste à 28 px, la bordure transparente agrandit la zone de tap). */
  @media (max-width:733.98px){
    .cn-inner { gap:12px; }
    .cn-burger, .cn-search { width:40px; height:40px; }
    .cn-home { width:40px; height:40px; }
    .cn-link { border-style:solid; border-color:transparent; border-width:6px 0; background-clip:padding-box; }
  }
`

const TAKEOVER_CLASS = "gd-cn-takeover"

const ChapterNav = ({ title, items, ctaLabel, ctaHref }) => {
  const { locale } = useContext(Locale)
  const barRef = useRef(null)
  const railRef = useRef(null)
  const [active, setActive] = useState(items[0]?.id)
  // identités instables (items inline dans les pages) : l'effet de scroll,
  // monté une seule fois, lit toujours la liste courante via cette ref
  const idsRef = useRef([])
  idsRef.current = items.map(i => i.id)
  // clic sur un chapitre : il reste actif pendant le défilement animé puis
  // tant que le lecteur ne s'éloigne pas de plus de 40 px de la cible (ancres
  // collées l'une à l'autre : le chapitre cliqué gagne)
  const lockRef = useRef(null)
  const [top, setTop] = useState(60)
  // Dérivé du contexte dès le rendu serveur : avec « /fr/ » en dur corrigé après
  // hydratation, le HTML servi de toutes les pages EN renvoyait vers la home
  // française (crawlers, clic avant hydratation). Constaté le 22/09/2026.
  const home = `/${locale === "en" ? "en" : "fr"}/`

  // Scroll / takeover : monté UNE SEULE fois — surtout pas dépendant de `items`
  // (identité instable, définie inline dans les pages) : sinon chaque re-rendu
  // démonte/remonte l'effet et la classe takeover est retirée par les cleanups.
  useEffect(() => {
    if (typeof window === "undefined") return undefined

    // se caler sous la barre sticky du site (fixed top-0 z-50)
    const siteBar = document.querySelector("div.fixed.inset-x-0.top-0.z-50")
    const headerH = siteBar?.offsetHeight || 60
    setTop(headerH)

    // takeover : position naturelle de la barre dans le document (mesurée hors
    // épinglage) ; au scroll, dès que la barre atteint son point d'épinglage,
    // la barre native coulisse (classe sur <html>) et la ChapterNav monte à top:0.
    const bar = barRef.current
    let natural = 0
    const measure = () => {
      // mesure fiable uniquement hors épinglage (au montage, la page est en
      // haut et la barre sous le héros ; en takeover on garde la mesure valide)
      if (!bar || document.documentElement.classList.contains(TAKEOVER_CLASS)) return
      natural = bar.getBoundingClientRect().top + window.scrollY
    }
    // chapitre actif : la dernière section dont le haut a franchi la ligne
    // située juste sous la barre collante (calculé dans le même rAF passif ;
    // l'ancien IntersectionObserver à bande médiane ratait les sections
    // courtes et restait figé sur « En bref » sur mobile). En bas de page,
    // la dernière section visible l'emporte (FAQ courte).
    const pickActive = () => {
      const line = (bar ? bar.getBoundingClientRect().bottom : 52) + 24
      const vh = window.innerHeight
      const atEnd = window.scrollY + vh >= document.documentElement.scrollHeight - 2
      // la section franchie la plus basse (indépendant de l'ordre des liens,
      // qui ne suit pas toujours l'ordre des sections dans la page)
      let cur = null
      let best = -Infinity
      for (const id of idsRef.current) {
        const el = document.getElementById(id)
        if (!el) continue
        const t = el.getBoundingClientRect().top
        if ((t <= line || (atEnd && t < vh)) && t > best) { best = t; cur = id }
      }
      return cur || idsRef.current[0]
    }
    let raf = 0
    const onScroll = () => {
      if (raf) return
      raf = window.requestAnimationFrame(() => {
        raf = 0
        const taken = natural > 0 && window.scrollY >= natural - headerH - 1
        document.documentElement.classList.toggle(TAKEOVER_CLASS, taken)
        setTop(taken ? 0 : headerH)
        const lock = lockRef.current
        if (lock) {
          const y = window.scrollY
          if (!lock.arrived && (Math.abs(y - lock.target) < 2 || Date.now() - lock.t > 1200)) { lock.arrived = true; lock.target = y }
          if (lock.arrived && Math.abs(y - lock.target) > 40) lockRef.current = null
        }
        const id = lockRef.current ? lockRef.current.id : pickActive()
        setActive(prev => (prev === id ? prev : id))
      })
    }
    measure()
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", measure)
    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", measure)
      if (raf) window.cancelAnimationFrame(raf)
      document.documentElement.classList.remove(TAKEOVER_CLASS)
    }
  }, [])

  // le rail défile horizontalement pour garder le lien actif visible (centré).
  // Jamais scrollIntoView : il ferait aussi défiler la page verticalement.
  useEffect(() => {
    const rail = railRef.current
    if (!rail || rail.scrollWidth <= rail.clientWidth + 1) return
    const link = rail.querySelector(".cn-link.active")
    if (!link) return
    const left = Math.max(0, Math.min(rail.scrollWidth - rail.clientWidth, link.offsetLeft - (rail.clientWidth - link.offsetWidth) / 2))
    if (Math.abs(left - rail.scrollLeft) < 2) return
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches
    rail.scrollTo({ left, behavior: reduce ? "auto" : "smooth" })
  }, [active])

  const go = id => {
    const el = document.getElementById(id)
    if (!el) return
    const offset = (barRef.current?.offsetHeight || 52) + 16
    const max = document.documentElement.scrollHeight - window.innerHeight
    const target = Math.max(0, Math.min(max, el.getBoundingClientRect().top + window.scrollY - offset))
    lockRef.current = { id, target, arrived: false, t: Date.now() }
    setActive(id)
    window.scrollTo({ top: target, behavior: "smooth" })
  }

  const openSiteMenu = () => {
    // événement déjà écouté par Header (gestion desktop/mobile incluse)
    window.dispatchEvent(new Event("header:open-menu"))
  }

  return (
    <div ref={barRef} className="cn-bar" style={{ top }}>
      <style>{STYLE}</style>
      <div className="cn-inner">
        <div className="cn-site">
          <button type="button" className="cn-burger" onClick={openSiteMenu} aria-label={home === "/en/" ? "Open menu" : "Ouvrir le menu"}>
            <span />
          </button>
          <a href={home} className="cn-home" aria-label="GADAIT International">
            <Logo className="text-blue h-[22px] w-[22px]" />
          </a>
        </div>
        <div className="cn-title">{title}</div>
        <div className="cn-links" ref={railRef}>
          {items.map(item => (
            <button key={item.id} type="button" data-target={item.id} aria-current={active === item.id ? "true" : undefined} className={`cn-link${active === item.id ? " active" : ""}`} onClick={() => go(item.id)}>
              {item.label}
            </button>
          ))}
        </div>
        {/* Loupe : la recherche du site reste à un geste, comme sur apple.com. */}
        <button
          type="button"
          className="cn-search"
          onClick={() => openSpotlight({ source: "chapter-nav", gesture: true })}
          onPointerEnter={prefetchSpotlight}
          aria-label={home === "/en/" ? "Search" : "Rechercher"}
        >
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M8.75 14.5a5.75 5.75 0 1 1 0-11.5 5.75 5.75 0 0 1 0 11.5ZM13 13l4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </button>
        {ctaLabel && (ctaHref ? (
          <a href={ctaHref} className="cn-cta" style={{ textDecoration:"none", display:"inline-flex", alignItems:"center" }}>{ctaLabel}</a>
        ) : (
          <button type="button" className="cn-cta" onClick={() => window.__openPageContactModal && window.__openPageContactModal()}>
            {ctaLabel}
          </button>
        ))}
      </div>
    </div>
  )
}

export default ChapterNav
